import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HbrSpinner } from '@harbor/elements-react-standalone';
import StbMarkdown from '../security-toolbox-ui/StbMarkdown';
import { DOCS_BASE, DocEntry, useDocContent } from './useDocsManifest';

interface DocsContentProps {
  entry: DocEntry;
  /** Heading anchor to scroll to once the page has rendered. */
  anchor?: string;
  /**
   * Called when the reader clicks a link to another document, e.g. `validate-pov.md`.
   * Without this the browser follows the relative href and leaves the app.
   */
  onNavigate?: (markdownFile: string) => void;
}

/**
 * "17 August 2026" from an ISO date, or null if it is unparseable.
 *
 * Readers get the date the page was documented and can judge for themselves whether it
 * predates a change they know about. An automatic staleness warning was tried first and
 * removed: the app publishes no version, so the only signal available was the UI git
 * sha, which changes on every commit and marked every page out of date forever.
 */
const documentedOn = (iso?: string): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

interface ZoomTarget {
  src: string;
  alt: string;
}

const DocsContent: React.FC<DocsContentProps> = ({ entry, anchor, onNavigate }) => {
  const { data, isLoading, error } = useDocContent(entry.path);
  const containerRef = useRef<HTMLDivElement>(null);

  // Screenshots are legible at capture size but not at drawer width, so any image can
  // be opened full-window. Delegated from the container so StbMarkdown stays generic.
  const [zoom, setZoom] = useState<ZoomTarget | null>(null);

  const handleContentClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;

      if (target instanceof HTMLImageElement) {
        setZoom({ src: target.currentSrc || target.src, alt: target.alt });
        return;
      }

      // Cross-references between documents are plain relative markdown links. Following
      // them would navigate the app away to a .md URL, so switch documents instead.
      const link = target.closest('a');
      const href = link?.getAttribute('href');
      if (href && !/^([a-z]+:)?\/\//.test(href) && href.split('#')[0].endsWith('.md')) {
        event.preventDefault();
        onNavigate?.(href);
      }
    },
    [onNavigate]
  );

  useEffect(() => {
    if (!zoom) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoom(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [zoom]);

  // Markdown references images as `assets/name.png`, relative to the document. The
  // drawer renders inside an app route, so those have to resolve against the bundle.
  const assetBase = `${DOCS_BASE}/${entry.path.split('/')[0]}/`;

  useEffect(() => {
    if (!anchor || !data || !containerRef.current) return;
    const target = containerRef.current.querySelector(`[data-anchor="${anchor}"]`);
    target?.scrollIntoView({ block: 'start' });
  }, [anchor, data]);

  useEffect(() => {
    if (data) containerRef.current?.scrollTo?.({ top: 0 });
  }, [entry.id, data]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <HbrSpinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="my-4 p-3 rounded border border-gray-300 text-sm">
        <p className="font-semibold mb-1">This page could not be loaded.</p>
        <p className="text-gray-600 m-0">
          {(error as Error)?.message || 'The documentation bundle may not be deployed.'}
        </p>
      </div>
    );
  }

  const documented = documentedOn(entry.capturedAt);

  return (
    <div
      ref={containerRef}
      className="docs-content"
      onClick={handleContentClick}
      role="presentation"
    >
      <StbMarkdown assetBase={assetBase}>{data}</StbMarkdown>

      {documented && <p className="docs-content__footnote">Documented {documented}</p>}

      {/* Portalled to the body: inside the drawer it would be clipped by the panel. */}
      {zoom &&
        createPortal(
          <div
            className="docs-zoom"
            role="dialog"
            aria-modal="true"
            aria-label={zoom.alt || 'Screenshot'}
            onClick={() => setZoom(null)}
          >
            <img src={zoom.src} alt={zoom.alt} />
            <p className="docs-zoom__hint">Click anywhere, or press Escape, to close</p>
          </div>,
          document.body
        )}
    </div>
  );
};

export default DocsContent;
